import { usePostHog } from 'posthog-js/react';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { routes } from './routes';
import { useOptionalUser } from './user-context';

/** `pending` until both the user and the shop id from the route are known. */
export type ShopAccess = 'pending' | 'allowed' | 'denied';

/**
 * Checks that the `:shopId` in the route is a valid shop the user has access
 * to, and redirects to not found or unauthorized when it is not. A route
 * component should render nothing while denied, so its view does not call the
 * API for a shop the user cannot see.
 */
export const useShopAccessGuard = (): ShopAccess => {
  const { user } = useOptionalUser();
  const { shopId } = useParams();
  const navigate = useNavigate();
  const posthog = usePostHog();

  const isValidShopId = shopId !== undefined && Number.isFinite(Number(shopId));
  const hasShopAccess = isValidShopId && user?.shops.includes(shopId);
  const access: ShopAccess =
    !user || !shopId ? 'pending' : hasShopAccess ? 'allowed' : 'denied';

  // biome-ignore lint/correctness/useExhaustiveDependencies: redirect only when the shop or the access changes, not on every user refetch
  useEffect(() => {
    if (access !== 'denied') {
      return;
    }

    if (!isValidShopId) {
      posthog.capture('invalid_shop_id', { shopId });
      navigate(routes.notFound, { replace: true });
      return;
    }

    posthog.capture('no_user_access_for_shop', { shopId });
    navigate(routes.unAuth, { replace: true });
  }, [shopId, access]);

  return access;
};
